"use client";

import { useRef, useEffect, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * 可滚动容器组件
 * 
 * 用于解决当 Dialog 或其他模态组件打开时，Radix UI 锁定 body 滚动导致容器无法滚动的问题。
 * 通过手动处理 wheel 事件来实现滚动功能，确保在 Dialog 打开时容器仍可正常滚动。
 * 
 * @example
 * ```tsx
 * <ScrollableContainer className="max-h-48 overflow-y-auto">
 *   <div>可滚动内容</div>
 * </ScrollableContainer>
 * ```
 */
export function ScrollableContainer({
  children,
  className,
  ...props
}: {
  children: ReactNode;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      // 阻止事件冒泡，避免被 Dialog 的滚动锁定机制拦截
      e.stopPropagation();

      // 手动实现滚动
      const deltaY = e.deltaY;
      const currentScrollTop = container.scrollTop;
      const maxScrollTop = container.scrollHeight - container.clientHeight;

      // 计算新的滚动位置
      const newScrollTop = Math.max(0, Math.min(maxScrollTop, currentScrollTop + deltaY));

      // 如果滚动位置会改变，则手动滚动并阻止默认行为
      if (newScrollTop !== currentScrollTop) {
        container.scrollTop = newScrollTop;
        e.preventDefault();
      } else {
        // 如果已经到达边界，阻止默认行为以避免触发 body 滚动
        if ((currentScrollTop === 0 && deltaY < 0) || (currentScrollTop >= maxScrollTop && deltaY > 0)) {
          e.preventDefault();
        }
      }
    };

    // 使用原生事件监听器，设置 { passive: false } 以允许调用 preventDefault
    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn("pointer-events-auto", className)}
      {...props}
    >
      {children}
    </div>
  );
}

